// Venue text + distance + walking-time formatters.
// Extracted from GuestMapSurface.js per FRONTEND_ARCHITECTURE.md.

import { displayForCategory } from '../../utils/venueCategories';

/**
 * Returns the user-facing subtitle for a venue card: the first three
 * selected categories joined by " · ". Falls back to the legacy
 * `venue.category` (or "Venue") when no categories are stored.
 */
export function formatVenueCategoryLabel(venue) {
  const raw = Array.isArray(venue?.categories) ? venue.categories.filter(Boolean) : [];
  if (raw.length > 0) {
    return raw.slice(0, 3).map(displayForCategory).join(' · ');
  }
  return venue?.category || 'Venue';
}

/**
 * Returns the owner's short card tagline as its OWN line beneath the
 * category subtitle. Suppressed when the backend has defaulted
 * `venue.description` to the category label (avoids duplicate text).
 */
export function formatVenueCardDescription(venue) {
  const description = typeof venue?.description === 'string' ? venue.description.trim() : '';
  if (!description || description === formatVenueCategoryLabel(venue)) return '';
  return description;
}

/**
 * Open/closed label rendered on every venue card variant. Uses the
 * backend-computed `is_open_now` + `opens`/`closes` fields.
 */
export function venueOpenStatusLabel(venue) {
  if (venue?.is_open_now === false) {
    return venue?.opens ? `Closed · opens ${venue.opens}` : 'Closed';
  }
  return venue?.closes ? `Open · closes ${venue.closes}` : 'Open now';
}

export function formatVenueDistance(userPosition, venue) {
  const sourceLat = userPosition?.[0];
  const sourceLng = userPosition?.[1];
  if (typeof sourceLat !== 'number' || typeof sourceLng !== 'number' || !venue?.latitude || !venue?.longitude) {
    return 'Nearby';
  }
  const toRad = (value) => (value * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(venue.latitude - sourceLat);
  const dLng = toRad(venue.longitude - sourceLng);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(sourceLat)) * Math.cos(toRad(venue.latitude)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = earthRadiusKm * c;
  if (distance < 1) {
    return `${Math.max(100, Math.round(distance * 1000 / 50) * 50)}m away`;
  }
  return `${distance.toFixed(distance < 10 ? 1 : 0)}km away`;
}

export function formatWalkingTime(userPosition, venue) {
  const sourceLat = userPosition?.[0];
  const sourceLng = userPosition?.[1];
  if (typeof sourceLat !== 'number' || typeof sourceLng !== 'number' || !venue?.latitude || !venue?.longitude) {
    return null;
  }
  const toRad = (value) => (value * Math.PI) / 180;
  const earthRadiusKm = 6371;
  const dLat = toRad(venue.latitude - sourceLat);
  const dLng = toRad(venue.longitude - sourceLng);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRad(sourceLat)) * Math.cos(toRad(venue.latitude)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const distance = earthRadiusKm * c;
  const minutes = Math.max(1, Math.round((distance / 5) * 60));
  return `${minutes} min walk`;
}
